import { formatDateShort } from '@shared/utils/date-formatter/date-formatter';

import { type CompanyListingPageContent } from '../get-company-listing-page-content';
import {
  type CompanyApprovedMemberDto,
  type CompanyListingResult,
  CompanyListingType,
  type CompanyMeetingRegistrantDto,
  type CompanyPendingMemberDto,
} from './get-company-listings/types';

export type TableColumn = {
  excelValueFormat?: string;
  formatValue?: (value: null | number | string) => null | number | string;
  formatValueExcel?: (value: null | number | string) => Date | null | number | string;
  key: string;
  label: string;
};
export type TableRow = Record<string, null | number | string>;
export type TableData = { columns: TableColumn[]; rows: TableRow[]; totalCount: number };
type HeaderLabels = CompanyListingPageContent['membersTableHeaderLabels'];
type TypedColumns<T> = ReadonlyArray<{
  excelValueFormat?: string;
  formatValue?: (value: null | number | string) => null | number | string;
  formatValueExcel?: (value: null | number | string) => Date | null | number | string;
  key: keyof T & string;
  label: string;
}>;

const EXCEL_DATE_TIME_FORMAT = 'mm/dd/yyyy hh:mm:ss';

const toExcelDate = (value: null | number | string): Date | null => {
  if (typeof value !== 'string' || !value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getApprovedMemberColumns = (
  headers: HeaderLabels,
  locale: string,
  membershipLevelLabels: Map<string, string>,
  membershipTypeLabels: Map<string, string>
): TableColumn[] =>
  [
    { key: 'mdrtId', label: headers.mdrtId },
    { key: 'firstName', label: headers.firstName },
    { key: 'lastName', label: headers.lastName },
    { key: 'addressLine', label: headers.addressLine },
    { key: 'city', label: headers.city },
    { key: 'state', label: headers.state },
    { key: 'country', label: headers.country },
    { key: 'phoneNumber', label: headers.phoneNumber },
    { key: 'agentId', label: headers.agentId },
    {
      formatValue: (value) =>
        typeof value === 'string' ? (membershipLevelLabels.get(value) ?? value) : value,
      key: 'membershipLevel',
      label: headers.membershipLevel,
    },
    {
      formatValue: (value) =>
        typeof value === 'string' ? (membershipTypeLabels.get(value) ?? value) : value,
      key: 'membershipType',
      label: headers.memberStatus,
    },
    { key: 'membershipYearsCount', label: headers.totalYearsOfMembership },
    {
      excelValueFormat: EXCEL_DATE_TIME_FORMAT,
      formatValue: (value) => formatDateShort(value, locale),
      formatValueExcel: (value) => toExcelDate(value),
      key: 'approvedDate',
      label: headers.approvedDate,
    },
  ] satisfies TypedColumns<CompanyApprovedMemberDto>;

const getPendingMemberColumns = (
  headers: HeaderLabels,
  statusIdToProblemMap: CompanyListingPageContent['statusIdToProblemMap']
): TableColumn[] =>
  [
    { key: 'mdrtId', label: headers.mdrtId },
    { key: 'firstName', label: headers.firstName },
    { key: 'lastName', label: headers.lastName },
    { key: 'addressLine', label: headers.addressLine },
    { key: 'city', label: headers.city },
    { key: 'state', label: headers.state },
    { key: 'country', label: headers.country },
    { key: 'phoneNumber', label: headers.phoneNumber },
    { key: 'agentId', label: headers.agentId },
    {
      formatValue: (value) => {
        if (typeof value !== 'number') return value;
        return statusIdToProblemMap.byId[value] ?? statusIdToProblemMap.fallback ?? value;
      },
      key: 'statusId',
      label: headers.problem,
    },
  ] satisfies TypedColumns<CompanyPendingMemberDto>;

const getMeetingRegistrantColumns = (
  headers: HeaderLabels,
  locale: string,
  membershipLevelLabels: Map<string, string>,
  membershipTypeLabels: Map<string, string>
): TableColumn[] =>
  [
    { key: 'mdrtId', label: headers.mdrtId },
    { key: 'firstName', label: headers.firstName },
    { key: 'lastName', label: headers.lastName },
    { key: 'addressLine', label: headers.addressLine },
    { key: 'city', label: headers.city },
    { key: 'state', label: headers.state },
    { key: 'country', label: headers.country },
    { key: 'phoneNumber', label: headers.phoneNumber },
    { key: 'agentId', label: headers.agentId },
    {
      formatValue: (value) =>
        typeof value === 'string' ? (membershipLevelLabels.get(value) ?? value) : value,
      key: 'membershipLevel',
      label: headers.membershipLevel,
    },
    {
      formatValue: (value) =>
        typeof value === 'string' ? (membershipTypeLabels.get(value) ?? value) : value,
      key: 'membershipType',
      label: headers.memberStatus,
    },
    { key: 'membershipYearsCount', label: headers.totalYearsOfMembership },
    {
      excelValueFormat: EXCEL_DATE_TIME_FORMAT,
      formatValue: (value) => formatDateShort(value, locale),
      formatValueExcel: (value) => toExcelDate(value),
      key: 'approvedDate',
      label: headers.approvedDate,
    },
    { key: 'attendeeStatus', label: headers.attendeeStatus },
  ] satisfies TypedColumns<CompanyMeetingRegistrantDto>;

const getColumns = (
  listType: CompanyListingType,
  headers: HeaderLabels,
  statusIdToProblemMap: CompanyListingPageContent['statusIdToProblemMap'],
  locale: string,
  membershipLevelLabels: Map<string, string>,
  membershipTypeLabels: Map<string, string>
): TableColumn[] => {
  switch (listType) {
    case CompanyListingType.ANNUAL_MEETING:
    case CompanyListingType.EDGE_MEETING:
    case CompanyListingType.GLOBAL_CONFERENCE:
      return getMeetingRegistrantColumns(
        headers,
        locale,
        membershipLevelLabels,
        membershipTypeLabels
      );
    case CompanyListingType.APPROVED_MEMBER:
      return getApprovedMemberColumns(headers, locale, membershipLevelLabels, membershipTypeLabels);
    case CompanyListingType.PENDING_MEMBER:
      return getPendingMemberColumns(headers, statusIdToProblemMap);
    default:
      return [];
  }
};

export const getTableData = (
  result: CompanyListingResult,
  headers: HeaderLabels,
  statusIdToProblemMap: CompanyListingPageContent['statusIdToProblemMap'],
  locale: string,
  membershipLevelLabels: Map<string, string>,
  membershipTypeLabels: Map<string, string>
): TableData => {
  const columns = getColumns(
    result.listType,
    headers,
    statusIdToProblemMap,
    locale,
    membershipLevelLabels,
    membershipTypeLabels
  );
  const rows = result.data.items.map((item) => {
    const row: TableRow = {};
    for (const { key } of columns) {
      row[key] = (item as Record<string, unknown>)[key] as null | number | string;
    }

    return row;
  });
  return { columns, rows, totalCount: result.data.totalCount };
};
